const RUNTIME_KEY_PATTERN = /^[a-zA-Z0-9_.:-]{1,120}$/
const LOCAL_PROJECT_ID_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/
const VALUE_LIMIT = 262_144
const NAMESPACE_LIMIT = 1_048_576
const KEY_LIMIT = 128

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const isUuid = (value: string) => UUID_PATTERN.test(value)

const byteLength = (value: string) => new TextEncoder().encode(value).byteLength

const projectPrefix = (projectId: string) => {
  if (!LOCAL_PROJECT_ID_PATTERN.test(projectId)) throw new Error('Invalid Appy project ID')
  return `appy.runtime.${encodeURIComponent(projectId)}.`
}

const storageKey = (projectId: string, key: string) => {
  if (!RUNTIME_KEY_PATTERN.test(key)) throw new Error('Invalid Appy storage key')
  return `${projectPrefix(projectId)}${key}`
}

export const localRuntimeGet = (projectId: string, key: string, storage: Storage = window.localStorage): unknown => {
  const raw = storage.getItem(storageKey(projectId, key))
  if (raw === null) return null
  try { return JSON.parse(raw) as unknown } catch { return raw }
}

export const localRuntimeSet = (projectId: string, key: string, value: unknown, storage: Storage = window.localStorage): void => {
  const target = storageKey(projectId, key)
  const serialized = JSON.stringify(value)
  if (serialized === undefined) throw new Error('Appy storage values must be valid JSON')
  const valueBytes = byteLength(serialized)
  if (valueBytes > VALUE_LIMIT) throw new Error('This saved value exceeds the 256 KB per-key limit')

  const prefix = projectPrefix(projectId)
  let keyCount = 0
  let namespaceBytes = 0
  for (let index = 0; index < storage.length; index += 1) {
    const storedKey = storage.key(index)
    if (!storedKey?.startsWith(prefix)) continue
    keyCount += 1
    if (storedKey !== target) namespaceBytes += byteLength(storage.getItem(storedKey) ?? '')
  }
  if (storage.getItem(target) === null && keyCount >= KEY_LIMIT) throw new Error('This app has reached its 128-key storage limit')
  if (namespaceBytes + valueBytes > NAMESPACE_LIMIT) throw new Error('This app has reached its 1 MB save-data limit')
  storage.setItem(target, serialized)
}

export const localRuntimeDelete = (projectId: string, key: string, storage: Storage = window.localStorage): void => {
  storage.removeItem(storageKey(projectId, key))
}
